import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Box, Typography } from '@mui/material';
import Dropdown, { DropdownOption } from '../ui/dropdown';
import TreeDropdown, { TreeDropdownOption } from '../tree-dropdown';
import OneTimeProvision from './parts/one-time-provision';
import AddBankAccount from './add-bank-account';

import useApi from '../../hooks/use-api';
import buildApiRequestConfig from '../../api/url-builder';
import routes from '../../api/routes';
import { useUser } from '../../providers/user-provider';
import { usePermissions } from '../../providers/permissions-provider';
import DeletePopup from './delete';

interface Transaction {
    amount: string;
    description: string;
    transactionDate: string;
    categoryId?: number;
    bankAccountId?: number;
}

// Existing manually-added transaction being edited - prefills the form and
// switches the title/button wording. The same onCreate callback fires on
// submit; the parent decides between create and update from whether it's
// currently editing something.
export interface EditableTransaction {
    amount: number | string;
    description: string;
    date: string;
    categoryId?: number | string | null;
    bankAccountId?: number | string | null;
}

interface AddTransactionPopupProps {
    open: boolean;
    onClose: () => void;
    onCreate: (data: Transaction) => void;
    isMobile?: boolean;
    editTransaction?: EditableTransaction | null;
}

const AddTransactionPopup: React.FC<AddTransactionPopupProps> = ({ open, onClose, onCreate, isMobile = false, editTransaction = null }) => {
    const { user } = useUser();
    const { can } = usePermissions();
    const [amount, setAmount] = useState('');
    const [description, setDescription] = useState('');
    const [date, setDate] = useState('');
    const [category, setCategory] = useState('');
    const [bank, setBank] = useState('');
    const [categoryOptions, setCategoryOptions] = useState<TreeDropdownOption[]>([]);
    const [bankOptions, setBankOptions] = useState<DropdownOption[]>([]);
    const [openModal, setOpenModal] = useState<'cancel' | ''>('');
    const [showAddBankModal, setShowAddBankModal] = useState(false);
    const [errors, setErrors] = useState<{ 
        amount?: string, 
        description?: string, 
        date?: string
    }>({});
    const [initialState, setInitialState] = useState({
        amount: '',
        description: '',
        date: '',
        category: '',
        bank: ''
    });

    const [fetchCategories, setFetchCategories] = useApi();
    const [fetchBanks, setFetchBanks] = useApi();
    const [createBanks, setCreateBanks] = useApi();

    React.useEffect(() => {
        if (!user) return;
        const { url, method } = buildApiRequestConfig(routes.bankAccounts.fetchByEntity, [{ param: 'entityId', value: user.entityId }]);
        setFetchBanks({ url, method });
        const { url: catUrl, method: catMethod } = buildApiRequestConfig(routes.categories.fetchByEntity, [{ param: 'entityId', value: user.entityId }]);
        setFetchCategories({ url: catUrl, method: catMethod });
    }, [open, user, setFetchBanks, setFetchCategories]);

    React.useEffect(() => {
        if (open) {
            // Blank form for a new transaction, or the existing values when editing
            const initial = {
                amount: editTransaction ? String(editTransaction.amount) : '',
                description: editTransaction?.description ?? '',
                date: editTransaction ? String(editTransaction.date).slice(0, 10) : '',
                category: editTransaction?.categoryId ? String(editTransaction.categoryId) : '',
                bank: editTransaction?.bankAccountId ? String(editTransaction.bankAccountId) : '',
            };
            setAmount(initial.amount);
            setDescription(initial.description);
            setDate(initial.date);
            setCategory(initial.category);
            setBank(initial.bank);
            setErrors({});
            // Save initial state for change tracking
            setInitialState(initial);
        }
    }, [open, editTransaction]);

    React.useEffect(() => {
        const { data, isLoading, error } = fetchCategories;
        if (data && !isLoading) {
            // Transform hierarchical data to TreeDropdownOption format
            const transformCategory = (cat: any): TreeDropdownOption => ({
                id: cat.id.toString(),
                label: cat.name,
                children: cat.children ? cat.children.map(transformCategory) : undefined
            });
            
            const hierarchicalOptions: TreeDropdownOption[] = data.map(transformCategory);
            setCategoryOptions(hierarchicalOptions);
        }
        if (error) {
            console.error('Error fetching categories:', error);
        }
    }, [fetchCategories]);

    React.useEffect(() => {
        const { data, isLoading, error } = fetchBanks;
        if (data && !isLoading) {
            const filteredBanks = data.filter((a: any) => a.bank.toLowerCase() === 'cash').map((item: any) => ({ value: String(item.id), label: `${item.bank} (${item.name})` }));
            const banksWithCreate: DropdownOption[] = [
                ...(can('transactions.addBankAccount', 'create') ? [{ value: '__add_new__', label: '+ Add New Bank Account' }] : []),
                ...filteredBanks
            ];
            setBankOptions(banksWithCreate);
            
            // Auto-select if only 1 option (after filtering, so excluding the add new option).
            // Not when editing - that would silently change the transaction's saved bank.
            if (filteredBanks.length === 1 && !editTransaction) {
                const selectedValue = filteredBanks[0].value;
                setBank(selectedValue);
                // Update initialState so it's not considered a change
                setInitialState(prev => ({ ...prev, bank: selectedValue }));
            }
        }
        if (error) {
            console.error('Error fetching banks:', error);
        }
    }, [fetchBanks]);

    React.useEffect(() => {
        const { isLoading } = createBanks;
        if (!isLoading && showAddBankModal === false) {
            // Banks were refreshed after creation, refetch them
            if (!user) return;
            const { url, method } = buildApiRequestConfig(routes.bankAccounts.fetchByEntity, [{ param: 'entityId', value: user.entityId }]);
            setFetchBanks({ url, method });
        }
    }, [createBanks, showAddBankModal, user, setFetchBanks]);

    const validate = () => {
        const newErrors: { 
            amount?: string, 
            description?: string,
            date?: string
        } = {};

        if (!amount) {
            newErrors.amount = 'Amount is required';
        }
        if (!description) {
            newErrors.description = 'Description is required';
        }
        if (!date) {
            newErrors.date = 'Date is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    const hasChanges = () => {
        return (
            amount !== initialState.amount ||
            description !== initialState.description ||
            date !== initialState.date ||
            category !== initialState.category ||
            bank !== initialState.bank
        );
    };

    const handleCreate = () => {
        setErrors({});
        if (validate()) {
            onCreate({ 
                amount, 
                description, 
                transactionDate: date, 
                bankAccountId: bank ? Number(bank) : undefined,
                categoryId: category ? Number(category) : undefined
            });
        }
    };

    const handleClose = () => {
        setAmount('');
        setDescription('');
        setDate('');
        setCategory('');
        setBank('');
        setErrors({});
        setOpenModal('');
        onClose();
    };

    const handleBankCreated = (data: { name: string; bankId: number | null; openingBalance: number }) => {
        if (!user) return;
        const { url, method } = buildApiRequestConfig(routes.bankAccounts.fetchByEntity, [{ param: 'entityId', value: user.entityId }]);
        setCreateBanks({ url, method });
        setShowAddBankModal(false);
    };

    return (
        <Dialog open={open} PaperProps={{ sx: { borderRadius: 3, minWidth: 600, p: 0.75 } }}>
            <AddBankAccount
                open={showAddBankModal}
                onClose={() => setShowAddBankModal(false)}
                onCreate={handleBankCreated}
                bankFilter={(bank: any) => bank.name && bank.name.toLowerCase() === 'cash'}
            />
            <DeletePopup
                open={openModal === 'cancel'}
                message="Discard Changes"
                description="All changes made in this form will be lost if you proceed"
                onCancel={() => setOpenModal('')}
                onConfirm={() => {
                    handleClose();
                }}
            />
            <DialogTitle sx={{ fontWeight: 700, fontSize: 22, pb: 1 }}>{editTransaction ? 'Edit Transaction' : 'Add New Transaction'}</DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
                <Box display="flex" gap={2} mb={4}>
                    <Box flex={1} position="relative">
                        <Typography sx={{ fontWeight: 500, fontSize: 15, mb: 0.5, color: 'var(--color-text-muted)' }}>
                            Transaction Amount <span style={{ color: '#F44336' }}>*</span>
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Enter Amount"
                            value={amount}
                            onChange={e => setAmount(e.target.value)}
                            error={!!errors.amount}
                            helperText={errors.amount}
                            sx={{ background: 'var(--color-page-background)', borderRadius: 2 }}
                        />
                        <Typography variant="caption" color="textSecondary" sx={{ position: 'absolute', bottom: -22, left: 0, width: 500 }}>
                            Enter a negative amount for expenses, or a positive amount for income.
                        </Typography>
                    </Box>
                    <Box flex={1}>
                        <Typography sx={{ fontWeight: 500, fontSize: 15, mb: 0.5, color: 'var(--color-text-muted)' }}>
                            Description <span style={{ color: '#F44336' }}>*</span>
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Enter Description"
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            error={!!errors.description}
                            helperText={errors.description}
                            sx={{ background: 'var(--color-page-background)', borderRadius: 2 }}
                        />
                    </Box>
                </Box>
                <OneTimeProvision date={date} setDate={setDate} errorDate={!!errors.date} helperText={errors.date} />
                <Box mb={2}>
                    <Typography sx={{ fontWeight: 500, fontSize: 15, mb: 0.5, color: 'var(--color-text-muted)' }}>
                        Category
                    </Typography>
                    <TreeDropdown
                        options={categoryOptions}
                        value={category}
                        onChange={(selectedId) => setCategory(selectedId || '')}
                        placeholder="Choose Category (Optional)"
                        height={40}
                        width="100%"
                    />
                </Box>
                <Box mb={2}>
                    <Typography sx={{ fontWeight: 500, fontSize: 15, mb: 0.5, color: 'var(--color-text-muted)' }}>
                        Bank Account
                    </Typography>
                    <Dropdown
                        options={bankOptions}
                        value={bank}
                        onChange={(val) => {
                            if (val === '__add_new__') {
                                setShowAddBankModal(true);
                            } else {
                                setBank(val);
                            }
                        }}
                        placeholder="Choose Bank Account (Optional)"
                        height={40}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
                <Button onClick={() => {
                    if (hasChanges()) {
                        setOpenModal('cancel');
                    } else {
                        handleClose();
                    }
                }} variant="contained" sx={{ background: 'var(--color-container-background)', color: 'var(--color-text-muted)', boxShadow: 'none', borderRadius: 2, fontWeight: 600, width: 120, '&:hover': { background: 'var(--color-container-background)' } }}>
                    Cancel
                </Button>
                <Button onClick={handleCreate} variant="contained" sx={{ textWrap: 'nowrap', background: 'var(--color-primary)', color: '#fff', boxShadow: 'none', borderRadius: 2, fontWeight: 600, width: 160, '&:hover': { background: 'var(--color-primary)' } }}>
                    {editTransaction ? 'Save Changes' : 'Add Transaction'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddTransactionPopup;
